'use client';

import { c as _c } from "react/compiler-runtime";
import { isSdkError, useGetMarketResolutionIntegrity } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { Skeleton } from '@/components/ui/app-skeleton';
import { logger } from '@/lib/telemetry/logger';
import { ResolutionIntegrityView } from './resolution-integrity-view';
const RESOLUTION_INTEGRITY_FIELDS = ['pubkey', 'resolvedAt', 'finalWindowSeconds', 'winningOutcomeIndex', 'outcomes', 'comparisons', 'score', 'activity'] as const;
const SKELETON_CARD_KEYS = ['one', 'two', 'three', 'four', 'five', 'six'] as const;

/**
 * Resolution data for a settled market (Democracy Score, winner-vs-others
 * cards, per-outcome table). The read is immutable after resolution, so it is
 * fetched once and never refreshed.
 */
export function ResolutionIntegritySection(t0) {
  const $ = _c(15);
  const {
    marketPubkey
  } = t0;
  const t = useTranslations("market.resolutionIntegrity");
  let t1;
  if ($[0] !== marketPubkey) {
    t1 = {
      pubkey: marketPubkey,
      fields: RESOLUTION_INTEGRITY_FIELDS
    };
    $[0] = marketPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      fetchOnce: true
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const query = useGetMarketResolutionIntegrity(t1, t2);
  let t3;
  let t4;
  if ($[3] !== marketPubkey || $[4] !== query.error || $[5] !== query.isError) {
    t3 = () => {
      if (!query.isError) {
        return;
      }
      logger.warn("resolution_integrity.load_failed", {
        marketPubkey,
        status: errorStatus(query.error)
      });
    };
    t4 = [marketPubkey, query.isError, query.error];
    $[3] = marketPubkey;
    $[4] = query.error;
    $[5] = query.isError;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  if (query.data !== undefined) {
    let t5;
    if ($[8] !== query.data) {
      t5 = <ResolutionIntegrityView data={query.data} />;
      $[8] = query.data;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  if (query.isError) {
    let t5;
    if ($[10] !== t) {
      t5 = t("loadFailed");
      $[10] = t;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== t5) {
      t6 = <p className="text-caption text-foreground-tertiary">{t5}</p>;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ResolutionIntegritySkeleton />;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function ResolutionIntegritySkeleton() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-5 w-40" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Skeleton className="size-32 shrink-0 self-center rounded-full" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div aria-hidden="true" data-testid="resolution-integrity-skeleton" className="flex flex-col gap-4 border-t border-border-secondary pt-4">{t0}<div className="flex flex-col gap-4 sm:flex-row sm:items-center">{t1}<div className="grid flex-1 grid-cols-2 gap-2 lg:grid-cols-3">{SKELETON_CARD_KEYS.map(_temp)}</div></div><Skeleton className="h-56" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp(key) {
  return <Skeleton key={key} className="h-16" />;
}
function errorStatus(error: Error | null): number | undefined {
  return isSdkError(error) && 'httpStatus' in error ? error.httpStatus : undefined;
}